/**
 * Backward-compatible facade over entityConfigLoader.
 *
 * All authoritative data has moved to src/shared/config/entities.config.json.
 * This module re-exports the same symbols under their original names so that
 * existing imports across the codebase continue to compile without changes.
 *
 * DO NOT add new logic here. Add it to entityConfigLoader.ts instead.
 */

import {
  getAllEntityConfigs,
  getUserEntityConfig,
  getEntityConfig,
  getFieldTypeMappings,
} from "./entityConfigLoader";

// ── Internal non-CRM entity type used for the user sub-item cache ─────────────
/** Bitrix24 User — resolved via user.get, not crm.item.list. Not a real CRM entity. */
export const USER_ENTITY_TYPE_ID: number = getUserEntityConfig().entityTypeId;

// ── Built-in entity catalog (derived from JSON) ───────────────────────────────

export const BUILT_IN_ENTITIES: ReadonlyArray<{
  entityTypeId: number;
  name: string;
  symbolCode: string;
  hasStages: boolean;
  hasPipelines: boolean;
  stageEntityIdPrefix: string | null;
}> = getAllEntityConfigs().map((e) => ({
  entityTypeId: e.entityTypeId,
  name: e.name,
  symbolCode: e.symbolCode,
  hasStages: e.platform.hasStages,
  hasPipelines: e.platform.hasPipelines,
  stageEntityIdPrefix: e.platform.stageEntityIdPrefix,
}));

/** Returns the catalog entry for a known built-in entity type, or null for custom SPAs. */
export function getEntityCatalogEntry(entityTypeId: number) {
  return getEntityConfig(entityTypeId);
}

/**
 * Returns the crm.status.list ENTITY_ID prefix for a given entity type.
 * Returns null for entityTypes without a stage model.
 * Returns `DYNAMIC_{id}_STAGE` for custom SPAs (id ≥ 128) not in the catalog.
 * When categoryId > 0 is supplied for a custom SPA, returns
 * `DYNAMIC_{id}_STAGE_{categoryId}` (named-pipeline stages).
 */
export function getStagePrefix(entityTypeId: number, categoryId?: number | null): string | null {
  const entry = getEntityConfig(entityTypeId);
  if (entry !== null) return entry.platform.stageEntityIdPrefix;
  // Custom SPA — default pipeline or named pipeline
  if (categoryId != null && categoryId > 0) {
    return `DYNAMIC_${entityTypeId}_STAGE_${categoryId}`;
  }
  return `DYNAMIC_${entityTypeId}_STAGE`;
}

/**
 * Whether the entity type supports pipeline categories.
 * Leads, Estimates, Contacts, and Companies use flat statuses.
 * Custom SPAs default to true (pipelines are the standard SPA model).
 */
export function hasPipelines(entityTypeId: number): boolean {
  return getEntityConfig(entityTypeId)?.platform.hasPipelines ?? true;
}

// ── Field-type → entity-type ID mapping (derived from JSON) ──────────────────

export const FIELD_TYPE_TO_ENTITY_ID_MAP: Readonly<Record<string, number>> = getFieldTypeMappings();
